import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { adminGet, adminPost, adminPut, adminDelete } from "../../../api/adminClient";
import Icon from "../../../components/AppIcon";
import { useToast } from "../../../hooks/use-toast";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import { InlineLoader } from "../../../components/ui/Loader";

const PermissionsManagement = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [formData, setFormData] = useState({ code: "", name: "", description: "", module: "", is_active: true });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  const loadPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGet("/admin/settings/permissions?include_inactive=true");
      setPermissions(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.status !== 401 && !e?.isSessionExpired) setError(e?.message || "Failed to load permissions");
      if (e?.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  const modules = [...new Set(permissions.map((p) => p.module).filter(Boolean))].sort();

  const getPermissionCategory = (module) =>
    module ? module.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") : "Other";

  const filteredPermissions = permissions.filter((p) => {
    const matchesSearch =
      (p.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule = !selectedModule || p.module === selectedModule;
    return matchesSearch && matchesModule;
  });

  const groupedPermissions = filteredPermissions.reduce((acc, perm) => {
    const cat = perm.module || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(perm);
    return acc;
  }, {});

  const handleCreatePermission = () => {
    setSelectedPermission(null);
    setFormData({ code: "", name: "", description: "", module: "", is_active: true });
    setFormErrors({});
    setEditModalOpen(true);
  };

  const handleEditPermission = (perm) => {
    setSelectedPermission(perm);
    setFormData({
      code: perm.code ?? "",
      name: perm.name ?? "",
      description: perm.description ?? "",
      module: perm.module ?? "",
      is_active: perm.is_active ?? true,
    });
    setFormErrors({});
    setEditModalOpen(true);
  };

  const handleDeletePermission = (perm) => {
    setSelectedPermission(perm);
    setDeleteModalOpen(true);
  };

  const handleSavePermission = async () => {
    const err = {};
    if (!formData.name?.trim()) err.name = "Name is required";
    if (!selectedPermission && !formData.code?.trim()) err.code = "Code is required";
    setFormErrors(err);
    if (Object.keys(err).length) return;
    setEditModalOpen(false);
    setLoading(true);
    try {
      const code = (formData.code || "").trim().toUpperCase().replace(/\s+/g, "_");
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        module: formData.module?.trim() || null,
        is_active: Boolean(formData.is_active),
      };
      if (selectedPermission) {
        await adminPut(`/admin/settings/permissions/${selectedPermission.permission_id}`, payload);
      } else {
        await adminPost("/admin/settings/permissions", { ...payload, code });
      }
      setSelectedPermission(null);
      setFormData({ code: "", name: "", description: "", module: "", is_active: true });
      success(selectedPermission ? "Permission updated successfully" : "Permission created successfully");
      loadPermissions();
    } catch (e) {
      setFormErrors((prev) => ({ ...prev, _submit: e?.message || "Save failed" }));
      setEditModalOpen(true);
      showError(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPermission) return;
    setDeleteModalOpen(false);
    setLoading(true);
    try {
      await adminDelete(`/admin/settings/permissions/${selectedPermission.permission_id}`);
      setSelectedPermission(null);
      success("Permission deleted successfully");
      loadPermissions();
    } catch (e) {
      showError(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Permissions</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage system permissions</p>
        </div>
        <div className="bg-card rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="flex flex-col items-center justify-center py-12">
            <InlineLoader text="Loading permissions…" size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Permissions</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage system permissions and their categories</p>
        </div>
        <button
          type="button"
          onClick={handleCreatePermission}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 shadow-md hover:shadow-lg transition-shadow shrink-0"
        >
          <Icon name="Plus" size={20} />
          Create Permission
        </button>
      </motion.div>

      {error && (
        <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-sm">{error}</div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="bg-card rounded-xl shadow-lg border border-border p-4 sm:p-6"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <div className="relative w-full sm:max-w-xl">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search permissions…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div className="flex flex-1 gap-3">
              <div className="relative w-full max-w-[220px]">
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">All Modules</option>
                  {modules.map((m) => (
                    <option key={m} value={m}>
                      {getPermissionCategory(m)}
                    </option>
                  ))}
                </select>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Icon name="Key" size={18} />
            <span>{filteredPermissions.length} permission{filteredPermissions.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
      </motion.div>

      {Object.keys(groupedPermissions).length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-card rounded-xl shadow-lg border border-border py-12 text-center"
        >
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Icon name="Key" size={28} className="text-primary" />
          </div>
          <p className="text-text-secondary">
            {searchTerm || selectedModule ? "No permissions found matching your filters." : "No permissions available."}
          </p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedPermissions)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([module, perms], idx) => (
              <motion.div
                key={module}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.05 + idx * 0.03 }}
                className="bg-card rounded-xl shadow-lg border border-border overflow-hidden"
              >
                <div className="px-4 sm:px-6 py-4 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Folder" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-text-primary">{getPermissionCategory(module)}</h2>
                      <p className="text-sm text-text-secondary">
                        {module === "other" ? "Uncategorized permissions" : `${getPermissionCategory(module)} related permissions`}
                      </p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 text-sm font-medium rounded-lg border border-border bg-muted/50">{perms.length}</span>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {perms.map((perm) => (
                      <div
                        key={perm.permission_id}
                        className="p-4 rounded-xl border border-border bg-card hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm font-mono break-all text-text-primary">{perm.code}</p>
                          </div>
                          <span
                            className={`flex-shrink-0 text-xs px-2 py-1 rounded ${
                              perm.is_active ? "text-success" : "text-text-secondary"
                            }`}
                          >
                            {perm.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        {perm.description && (
                          <p className="text-xs text-text-secondary mt-2">{perm.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                          <button
                            type="button"
                            onClick={() => handleEditPermission(perm)}
                            title="Edit"
                            className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                          >
                            <Icon name="Edit" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePermission(perm)}
                            title="Delete"
                            className="p-2 rounded-lg hover:bg-muted text-error transition-colors"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
        </div>
      )}

      {/* Edit/Create Permission Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setEditModalOpen(false)}>
          <div className="bg-card rounded-xl shadow-lg border border-border p-6 w-full max-w-lg admin-layout text-text-primary" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">{selectedPermission ? "Edit Permission" : "Create Permission"}</h3>
              <button type="button" onClick={() => setEditModalOpen(false)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            {formErrors._submit && (
              <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">{formErrors._submit}</div>
            )}
            <div className="space-y-4">
              {!selectedPermission ? (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Permission Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })
                    }
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary font-mono"
                    placeholder="e.g. PRODUCT_VIEW"
                  />
                  {formErrors.code && <p className="text-xs text-error mt-1">{formErrors.code}</p>}
                  <p className="text-xs text-text-secondary mt-1">Format: UPPERCASE_LETTERS_NUMBERS_UNDERSCORES</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Permission Code</label>
                  <p className="font-mono text-sm p-2 bg-muted rounded-lg">{selectedPermission.code}</p>
                  <p className="text-xs text-text-secondary mt-1">Code cannot be changed after creation</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                />
                {formErrors.name && <p className="text-xs text-error mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Module</label>
                <input
                  type="text"
                  value={formData.module}
                  onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                  placeholder="e.g. Products"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-border"
                />
                <span className="text-sm text-text-primary">Active</span>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setEditModalOpen(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePermission}
                  disabled={!formData.name?.trim() || (!selectedPermission && !formData.code?.trim())}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {selectedPermission ? "Update Permission" : "Create Permission"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen && !!selectedPermission}
        title="Delete Permission"
        message={`Are you sure you want to delete the permission "${selectedPermission?.code}"? Roles that have this permission will lose access.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setSelectedPermission(null); }}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default PermissionsManagement;
